<script setup lang="ts">
import type { TemperatureUnit } from "@/stores/settings.store";
import { computed } from "vue";
import LoadingValue from "@/components/ui/LoadingValue.vue";
import { Skeleton } from "@/components/ui/skeleton";
import { Droplets, Wind, Gauge, CloudOff, MapPin } from "@lucide/vue";
import {
  weatherDescription,
  type MainWeatherInfo,
  type ObservedRainWindow,
} from "rainowls-shared";
import WeatherSourceInfo from "./WeatherSourceInfo.vue";
import CardErrorState from "@/components/ui/CardErrorState.vue";
import WeatherIconComplex from "./WeatherIconComplex.vue";
import RainfallPanel from "./RainfallPanel.vue";
import ConditionValueBar from "./ConditionValueBar.vue";
import CurrentConditionMetric from "./CurrentConditionMetric.vue";
import { finiteMeasurement, measurementRange } from "@/utils/conditionBar";
import { useUnitConversion } from "@/composables/useUnitConversion";
import { sanitizeValue, sanitizeWindSpeed } from "@/utils/weatherSanitizer";

const props = defineProps<{
  wallMode?: boolean;
  weather: MainWeatherInfo | null;
  rainObservations?: ObservedRainWindow | null;
  unit: TemperatureUnit;
  loading?: boolean;
  error?: boolean;
}>();

const { convertTemperature, convertSpeed, labels } = useUnitConversion(
  () => props.unit,
);

// A background refresh keeps the current readings. Only an absent snapshot needs skeletons.
const showSkeleton = computed(() => !!props.loading && !props.weather);
// Keep the same inert layout behind an error so a failed first load cannot shrink the card.
const showError = computed(() => !props.loading && !props.weather);

const direction = computed(() => {
  const bearing = sanitizeValue(props.weather?.windDirection);
  return bearing === null
    ? ""
    : ["N", "NE", "E", "SE", "S", "SW", "W", "NW"][
        Math.round(bearing / 45) % 8
      ];
});

const wind = computed(() =>
  convertSpeed(sanitizeWindSpeed(props.weather?.windSpeed)),
);

const humidity = computed(() => sanitizeValue(props.weather?.humidity));
const pressure = computed(() => sanitizeValue(props.weather?.pressure));

const daily = computed(() =>
  props.weather?.dailyWeather?.days?.length ? props.weather.dailyWeather : null,
);

const stationTime = computed(() => {
  const time = props.weather?.currentObservationTimestamp
    ? props.weather.stationFormattedTime
    : null;
  return time && !time.includes("NaN") ? time : null;
});

// Consume the shared daily aggregates directly. These are presentation inputs,
// not a second implementation of forecast/extreme aggregation.
const metricBars = computed(() => {
  const w = props.weather;
  const forecast =
    daily.value && w?.dataSource !== "nws_fallback" ? daily.value : null;
  /** Read the observed daily minimum and maximum for a metric. */
  const observed = (
    key: "temperature" | "humidity" | "windSpeed" | "pressure",
  ) => measurementRange(w?.dailyMinimums?.[key], w?.dailyMaximums?.[key]);
  return {
    temperature: {
      observed: observed("temperature"),
      forecast: forecast
        ? measurementRange(
            forecast.todayMinTemperature,
            forecast.todayMaxTemperature,
          )
        : null,
    },
    humidity: {
      observed: observed("humidity"),
      forecast: forecast
        ? measurementRange(forecast.todayHumidityLow, forecast.todayHumidity)
        : null,
    },
    wind: {
      observed: observed("windSpeed"),
      forecast: forecast ? measurementRange(0, forecast.todayWindSpeed) : null,
    },
    pressure: { observed: observed("pressure"), forecast: null },
  };
});

/** Format an available temperature in the selected units. */
function temperature(value: number | undefined) {
  return convertTemperature(sanitizeValue(value));
}
</script>
<template>
  <section
    class="conditions-strip relative z-2 bg-app-conditions"
    :class="
      wallMode
        ? 'h-full min-h-0 px-[18px] pt-[22px] pb-2 short-wall:px-3.5 short-wall:pt-5 short-wall:pb-1.5 compact-wall:pt-2 compact-wall:pb-2 tall-wall:px-6 tall-wall:pt-[30px] tall-wall:pb-5'
        : 'px-7 py-3.5 max-[600px]:px-5 max-[600px]:pt-6 min-[1200px]:[--current-icon-size:120px]'
    "
    :aria-busy="loading"
    aria-label="Current conditions"
  >
    <p v-if="showSkeleton" class="sr-only" role="status">
      Loading current conditions…
    </p>
    <CardErrorState
      v-if="showError"
      class="absolute inset-0 z-10 rounded-[inherit] bg-app-conditions"
      :icon="error ? CloudOff : MapPin"
      :message="
        error ? 'Current conditions unavailable' : 'No current observations'
      "
      description="Current measurements aren’t available for this location right now. Try another location or refresh later."
    />
    <div
      class="contents"
      :class="{ invisible: showError }"
      :inert="showError || undefined"
      :aria-hidden="showError || undefined"
    >
      <WeatherSourceInfo
        v-if="weather"
        :source="
          weather?.isRainOwlsStation
            ? 'RainOwls station'
            : weather?.currentObservationSource === 'open-meteo'
              ? 'Weather model'
              : 'Station'
        "
        :station-id="
          weather?.stationId && weather?.stationId !== 'Invalid'
            ? weather?.stationId
            : 'Unavailable'
        "
        :timestamp="
          stationTime
            ? (weather?.currentObservationSource === 'open-meteo'
                ? 'Model data '
                : 'Observed ') + stationTime
            : 'Unavailable'
        "
      />
      <div
        class="grid items-center"
        :class="
          wallMode
            ? 'h-full grid-cols-1 grid-rows-[auto_auto_minmax(0,1fr)] gap-2 compact-wall:gap-0 tall-wall:gap-[18px]'
            : 'grid-cols-[minmax(0,1.05fr)_minmax(0,1fr)_minmax(0,1.3fr)] gap-8 max-[1200px]:grid-cols-2 max-[600px]:grid-cols-1 max-[600px]:gap-[26px]'
        "
      >
        <div
          class="flex min-w-0 flex-col justify-center"
          :class="wallMode ? 'gap-0' : 'gap-4'"
        >
          <div
            class="grid min-w-0 grid-cols-[auto_auto_minmax(0,1fr)] items-center gap-x-4 max-[1200px]:gap-x-2"
            :class="
              wallMode
                ? 'justify-center [--current-icon-size:64px] tall-wall:[--current-icon-size:72px]'
                : 'justify-start [--current-icon-size:64px] max-[400px]:grid-cols-[auto_minmax(0,1fr)] min-[1200px]:[--current-icon-size:88px]'
            "
          >
            <Skeleton
              v-if="showSkeleton"
              class="shrink-0 rounded-full"
              :style="{
                width: 'var(--current-icon-size)',
                height: 'var(--current-icon-size)',
              }"
              aria-hidden="true"
            />
            <CloudOff
              v-else-if="!weather || weather.weatherCode < 0"
              aria-hidden="true"
              class="text-text-muted"
              :style="{
                width: 'var(--current-icon-size)',
                height: 'var(--current-icon-size)',
              }"
            />
            <WeatherIconComplex
              v-else
              :weather-code="weather?.weatherCode ?? -1"
              :wind-speed="sanitizeWindSpeed(weather?.windSpeed)"
              :is-day="weather?.isDay ?? true"
              :size="88"
              :style="{
                width: 'var(--current-icon-size)',
                height: 'var(--current-icon-size)',
              }"
            />
            <div class="max-w-[16ch] min-w-0 max-[1200px]:min-w-max">
              <h2 class="sr-only">
                Current conditions for {{ weather?.locationName }}
              </h2>
              <div
                class="leading-none font-bold tracking-[-0.055em] tabular-nums"
                :class="
                  wallMode
                    ? 'text-[72px] short-wall:text-[60px] compact-wall:text-[56px]'
                    : 'text-[clamp(64px,5vw,88px)] max-[1200px]:text-[58px]'
                "
              >
                <LoadingValue :loading="showSkeleton">
                  {{
                    showSkeleton
                      ? "00"
                      : (temperature(weather?.temperature) ?? "—")
                  }}<span
                    v-if="
                      showSkeleton || temperature(weather?.temperature) !== null
                    "
                    >°</span
                  >
                </LoadingValue>
              </div>
              <p
                class="mt-1 h-[2.5em] leading-tight font-semibold"
                :class="wallMode ? 'text-base tall-wall:text-xl' : 'text-lg'"
                :title="
                  weather && weather.weatherCode >= 0
                    ? weatherDescription(weather.weatherCode)
                    : undefined
                "
              >
                <LoadingValue :loading="showSkeleton" class="max-w-full">
                  <span class="line-clamp-2">{{
                    showSkeleton
                      ? "Partly cloudy"
                      : !weather || weather.weatherCode < 0
                        ? "Sky conditions unavailable"
                        : weatherDescription(weather.weatherCode)
                  }}</span>
                </LoadingValue>
              </p>
            </div>
            <dl
              class="grid grid-cols-[max-content_max-content] items-baseline gap-x-3 gap-y-1 justify-self-start text-text-muted tabular-nums [&_dd]:text-right [&_dd]:font-medium [&_dd]:text-text-primary"
              :class="
                wallMode
                  ? 'text-xs tall-wall:text-sm'
                  : 'text-sm max-[1200px]:text-xs max-[400px]:col-start-2 max-[400px]:mt-2 max-[400px]:justify-start'
              "
            >
              <dt>Feels like</dt>
              <dd>
                <LoadingValue :loading="showSkeleton"
                  >{{
                    showSkeleton
                      ? "00"
                      : (temperature(weather?.apparentTemperature) ?? "—")
                  }}°</LoadingValue
                >
              </dd>
              <dt>High</dt>
              <dd>
                <LoadingValue :loading="showSkeleton"
                  >{{
                    showSkeleton
                      ? "00"
                      : (temperature(daily?.todayMaxTemperature) ?? "—")
                  }}°</LoadingValue
                >
              </dd>
              <dt>Low</dt>
              <dd>
                <LoadingValue :loading="showSkeleton"
                  >{{
                    showSkeleton
                      ? "00"
                      : (temperature(daily?.todayMinTemperature) ?? "—")
                  }}°</LoadingValue
                >
              </dd>
            </dl>
          </div>
          <div v-if="!wallMode" class="min-w-0 pl-[104px] max-[400px]:px-0">
            <ConditionValueBar
              class="max-w-72"
              :unit-preference="unit"
              :loading="showSkeleton"
              label="Temperature"
              kind="temperature"
              :current="finiteMeasurement(weather?.temperature)"
              v-bind="metricBars.temperature"
              :scale="[0, 100]"
              unit="°F"
            />
          </div>
        </div>
        <div
          class="grid min-w-0"
          :class="
            wallMode
              ? 'row-start-3 grid-cols-3 content-center items-start gap-3 pt-1 tall-wall:gap-5'
              : 'gap-[15px] max-[1200px]:pt-[18px] max-[600px]:gap-[18px] max-[600px]:pt-0'
          "
        >
          <CurrentConditionMetric
            :loading="showSkeleton"
            label="Wind"
            :icon="Wind"
            color="var(--weather-wind-text)"
            :value="`${direction} ${wind ?? '—'}`"
            :value-unit="labels.speed"
            kind="wind"
            :current="sanitizeWindSpeed(weather?.windSpeed)"
            v-bind="metricBars.wind"
            :scale="[0, 30]"
            bar-unit="mph"
            :unit-preference="unit"
            :wall-mode="wallMode"
            full-width
          />
          <CurrentConditionMetric
            :loading="showSkeleton"
            label="Humidity"
            :detail="
              showSkeleton
                ? 'Dew point'
                : `Dew point ${temperature(weather?.dewPoint) ?? '—'}°`
            "
            :icon="Droplets"
            color="var(--weather-humidity-text)"
            :value="humidity === null ? '—' : `${Math.round(humidity)}%`"
            kind="humidity"
            :current="finiteMeasurement(weather?.humidity)"
            v-bind="metricBars.humidity"
            :scale="[0, 100]"
            bar-unit="%"
            :unit-preference="unit"
            :wall-mode="wallMode"
          />
          <CurrentConditionMetric
            :loading="showSkeleton"
            label="Pressure"
            :icon="Gauge"
            color="var(--weather-pressure-text)"
            :value="pressure === null ? '—' : `${Math.round(pressure)}`"
            value-unit="mb"
            kind="pressure"
            :current="finiteMeasurement(weather?.pressure)"
            v-bind="metricBars.pressure"
            :scale="[980, 1040]"
            bar-unit="mb"
            :unit-preference="unit"
            :wall-mode="wallMode"
          />
        </div>
        <RainfallPanel
          :loading="showSkeleton"
          :wall-mode="wallMode"
          :unit="unit"
          class="min-w-0"
          :class="
            wallMode
              ? 'col-start-1 row-start-2 border-y border-[var(--conditions-divider)] py-1'
              : 'pt-[18px] max-[1200px]:col-span-full max-[1200px]:pt-1 max-[600px]:col-auto'
          "
          :rate="weather?.rainRate"
          :today="weather?.rainDay"
          :storm="weather?.rainStorm"
          :forecast-storm="weather?.forecastRainStorm"
          :observations="rainObservations"
          :time-zone="weather?.timeZoneIdentifier || weather?.timeZone"
        />
      </div>
    </div>
  </section>
</template>
